import { catalogLabel } from '../../api/marcCatalogService';
import { getMarcSubfield } from './useMarcCatalog';
/** Read the cs string from a Label (or empty string if missing). */
export var readCsLabel = function (label) {
    var _a;
    if (!label)
        return '';
    if (typeof label === 'string')
        return label;
    return (_a = label.cs) !== null && _a !== void 0 ? _a : '';
};
/**
 * Build the next Label value given the existing one and the new cs text.
 * Preserves an `en` translation when present so a partial edit doesn't
 * silently drop the English string.
 */
export var writeCsLabel = function (existing, csText) {
    if (existing && typeof existing === 'object' && existing.en !== undefined) {
        return { cs: csText, en: existing.en };
    }
    return csText;
};
/**
 * Same as {@link writeCsLabel}, except a blank input means "inherit" and
 * yields `undefined` — the caller is expected to DELETE the key rather than
 * store an empty string (CZNA-544).
 *
 * Inheritance is expressed by the absence of the key: the backend's
 * `FormConfigCompiler.fillInLabel` only falls through to the ontology when the
 * template has no `label`/`labelKey`, and it tests `hasNonNull`, which is true
 * for `""`. So a template carrying `label: ""` does NOT inherit — it renders a
 * nameless field in the form (`LabelTraceService.SOURCE_TEMPLATE_BLANK`), and
 * on top of that violates the form-config schema (`$defs/label` has
 * `minLength: 1`), which disables Save. Writing `""` therefore gives the user
 * neither the old name nor the inherited one; only removing the key does what
 * clearing the input looks like it should do.
 *
 * The whole label goes, `en` included: `en` cannot survive on its own (the
 * schema requires `cs` in the object form), and "inherit cs but keep this
 * hand-written en" is not a state the compiler can represent.
 *
 * Whitespace-only counts as blank — the schema would accept `" "`, but a form
 * label of one space is not a name. The non-blank value is stored verbatim
 * (untrimmed), so typing a trailing space mid-word does not fight the caret.
 * The one visible consequence is that a label cannot BEGIN with a space: the
 * first keystroke is blank, the key is deleted, and the controlled input
 * repaints empty. A leading space in a form label is not a thing anyone wants,
 * so this is left as is rather than tracked with a separate "user cleared it"
 * flag.
 */
export var writeCsLabelOrInherit = function (existing, csText) { return (csText.trim() === '' ? undefined : writeCsLabel(existing, csText)); };
/**
 * CZNA-566 — the name a subfield shows in the form when the template authors
 * none, or `''` when there is no such name to advertise.
 *
 * Mirrors `FormConfigCompiler.compileSubfield` exactly, because the editor must
 * not promise an inheritance the compiler does not perform. There are three
 * ways the compiler never reaches `fillInLabel` for a subfield, and each one
 * yields `''` here:
 *
 * - `formOnly: true` — `compileSubfield` returns BEFORE the catalog lookup; the
 *   name is whatever the template says, or nothing.
 * - a code the catalog does not list under this tag (`400$c`) — `catalogSub`
 *   is null and `fillInLabel` is never called.
 * - `labelKey` — `fillInLabel` short-circuits on it, and the renderer's
 *   `resolveLabel` falls back to it, same as at field level (CZNA-544).
 *
 * Plus one case where `fillInLabel` IS called and still inherits nothing: a
 * PRESENT but blank `label` (`""`, `{ cs: "" }`, whitespace). Its guard is
 * `hasNonNull("label")`, which is true for all of those, so the form shows a
 * nameless subfield — advertising the ontology name next to an input that looks
 * empty would be the one promise the compiler visibly breaks. (Clearing the
 * input deletes the key, after which the name DOES inherit and reappears here.)
 * And a blank catalog name is no name: the compiler checks `isBlank()`.
 *
 * Otherwise the result does NOT depend on whether `label` is authored: an
 * authored label wins in the compiler, but the admin still needs to know what
 * clearing it would give them. Callers decide where an authored label hides the
 * inherited one. `''` also while the catalog is still loading — "no name to
 * advertise" is the honest reading of "not known yet".
 */
export var inheritedSubfieldLabel = function (catalog, tag, subfield) {
    var _a;
    if (subfield.formOnly || subfield.labelKey != null)
        return '';
    if (subfield.label != null && readCsLabel(subfield.label).trim() === '')
        return '';
    var name = catalogLabel((_a = getMarcSubfield(catalog, tag, subfield.code)) === null || _a === void 0 ? void 0 : _a.label);
    return name.trim() === '' ? '' : name;
};
